import { Tooltip } from "react-tooltip";

import { dayjs } from "@/utils/date";

import type { ReportItem } from "../types";
import { getEffectiveStatus, getEffectiveStatusConfig, getProblemIndicators } from "../utils";
import { DiagnosticCauseBadge } from "./ExecutionDiagnostics";
import { getRowColorClass, StatusBadgeFromItem } from "./StatusBadge";

interface ReportTableProps {
  reportData: ReportItem[];
  onItemClick: (item: ReportItem) => void;
}

/**
 * Report results table with keyboard navigation and accessibility
 */
export function ReportTable({ reportData, onItemClick }: ReportTableProps) {
  if (reportData.length === 0) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>, item: ReportItem) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onItemClick(item);
    }
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4 px-2">
      <div className="flex items-center justify-between mb-4 px-2">
        <h2 className="text-base font-semibold text-gray-900">Resultados ({reportData.length} execuções)</h2>
      </div>

      {/* Data Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" aria-label="Tabela de execuções do plano de irrigação">
          <caption className="sr-only">Lista de execuções de planos de irrigação com status e horários</caption>
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-2 font-medium text-gray-700">Plano</th>
              <th className="text-left py-3 px-2 font-medium text-gray-700">Data</th>
              <th className="text-left py-3 px-2 font-medium text-gray-700">Previsto</th>
              <th className="text-left py-3 px-2 font-medium text-gray-700">Real</th>
              <th className="text-left py-3 px-2 font-medium text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody>
            {reportData.map((item, index) => {
              const effectiveStatusConfig = getEffectiveStatusConfig(item);
              const effectiveStatus = getEffectiveStatus(item);
              const problems = getProblemIndicators(item);
              const rowColorClass = getRowColorClass(effectiveStatus);
              const hasProblems = problems.length > 0 && effectiveStatus === "PROBLEMAS";
              const tooltipId = `tooltip-${item.irrigation_plan}-${index}`;

              return (
                <tr
                  key={`${item.irrigation_plan}-${index}`}
                  onClick={() => onItemClick(item)}
                  onKeyDown={(e) => handleKeyDown(e, item)}
                  className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${rowColorClass}`}
                  tabIndex={0}
                  role="button"
                  aria-label={`Ver detalhes da execução do plano ${item.planName} em ${dayjs(item.expected_start_time).format("DD/MM/YYYY")}`}
                  data-tooltip-id={hasProblems ? tooltipId : undefined}
                  data-tooltip-content={hasProblems ? problems.map((p) => p.label).join(", ") : undefined}
                >
                  <td className="py-3 px-2">
                    <div className="font-medium text-gray-900">{item.planName}</div>
                  </td>
                  <td className="py-3 px-2 text-gray-900">{dayjs(item.expected_start_time).format("DD/MM/YYYY")}</td>
                  <td className="py-3 px-2 text-gray-900">{dayjs(item.expected_start_time).format("HH:mm")}</td>
                  <td className="py-3 px-2 text-gray-900">
                    {item.actual_start_time ? dayjs(item.actual_start_time).format("HH:mm") : "-"}
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex flex-wrap items-center gap-1">
                      <StatusBadgeFromItem item={item} />
                      <DiagnosticCauseBadge diagnostics={item.diagnostics} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tooltip for problem indicators */}
      <Tooltip className="max-w-xs bg-neutral-50! border border-gray-200 rounded-lg shadow-lg text-sm text-gray-700! leading-relaxed" />
    </section>
  );
}
